<!--
© Copyright 2015-2026 Personalized Privacy Assistant Project, Carnegie Mellon University - All Rights Reserved - US Patent 11,768,949, US Patent 10,956,586, US Patent 12,235,973, and additional USPTO and EPO patents pending.
This code is proprietary and confidential. For any questions about this code, including licensing, please contact Prof. Norman Sadeh (sadeh@cs.cmu.edu).
-->
<template>
  <div>
    <!-- Add Admin User Modal -->
    <div class="modal fade" id="addAdminModal" tabindex="-1" role="dialog" aria-labelledby="addAdminModalLabel" aria-hidden="true" style="z-index:999999;">
      <div class="modal-dialog modal-lg" role="document" style="max-width: 750px;">
        <div class="modal-content">
          <div class="modal-header border-0" style="display: flex; justify-content: flex-end; padding: 15px 20px;">
            <button type="button" class="close" data-dismiss="modal" aria-label="Cancel" style="position: static; margin-left: auto;">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <div class="modal-body" style="padding: 0 30px 30px 30px;">
            <h4 style="color: #0051BA; font-weight: bold; margin-bottom: 20px;">Add Admin User</h4>
            <p style="font-size: 16px; line-height: 1.6; margin-bottom: 25px;">
              Enter the email address of the user you want to add as an admin to this registry.
              The user must have an existing IoTPI account and cannot already be an admin or owner of this registry.
            </p>

            <form id="addAdminForm" @submit.prevent="submitAddAdmin">
              <div class="form-group" style="display: flex; align-items: flex-start; gap: 12px; margin-bottom: 12px;">
                <label for="admin_email" style="font-weight: bold; font-size: 16px; color: #333; white-space: nowrap; padding-top: 8px; flex-shrink: 0;">New Admin Email</label>
                <div style="flex-grow: 1;">
                  <input 
                    type="email" 
                    id="admin_email" 
                    name="admin_email" 
                    v-model="adminEmail"
                    placeholder="" 
                    required="required" 
                    class="form-control" 
                    @input="validateEmail" 
                    style="margin-bottom: 8px;">
                  <small id="admin_email-help" class="form-text text-muted">Note: The email address must match their IoTPI account email</small>
                  <div v-if="errorMessage" class="alert alert-danger mt-2" role="alert">
                    {{ errorMessage }}
                  </div>
                  <div v-if="successMessage" class="alert alert-success mt-2" role="alert">
                    {{ successMessage }}
                  </div>
                </div>
              </div>

              <div class="d-flex gap-2 pt-2" style="gap: 10px;">
                <button type="button" class="btn btn-danger" data-dismiss="modal">Cancel</button>
                <button 
                  type="submit" 
                  id="add-admin-form-button" 
                  class="btn btn-registry" 
                  :disabled="!isEmailValid || isSubmitting" 
                  :style="{opacity: (isEmailValid && !isSubmitting) ? 1 : 0.6, cursor: (isEmailValid && !isSubmitting) ? 'pointer' : 'not-allowed'}">
                  {{ isSubmitting ? 'Adding...' : 'Add Admin' }}
                </button>
              </div>
            </form>
          </div>

        </div>
      </div>
    </div>

    <!-- Revoke Registry Access Modal -->
    <div class="modal fade" id="revokeRegistryAccessModal" tabindex="-1" role="dialog" aria-labelledby="revokeRegistryAccessModalLabel" aria-hidden="true" style="z-index:999999;">
      <div class="modal-dialog modal-lg" role="document" style="max-width: 750px;">
        <div class="modal-content">
          <div class="modal-header border-0" style="display: flex; justify-content: flex-end; padding: 15px 20px;">
            <button type="button" class="close" data-dismiss="modal" aria-label="Cancel" style="position: static; margin-left: auto;">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <div class="modal-body" style="padding: 0 30px 30px 30px;">
            <h4 id="revokeRegistryAccessModalLabel" style="color: #0051BA; font-weight: bold; margin-bottom: 20px;">Revoke Registry Access</h4>
            <p style="font-size: 16px; line-height: 1.6; margin-bottom: 25px;">
              By revoking registry access, you will remove the user's access to this registry and any resources associated with it.
            </p>

            <div class="custom-control custom-checkbox" style="margin-bottom: 25px; padding-left: 30px; display: flex; align-items: center; gap: 12px;">
              <input
                id="revoke_acknowledgement"
                v-model="revokeAcknowledged"
                type="checkbox"
                class="custom-control-input"
                style="width: 18px; height: 18px; margin-top: 0;">
              <label
                for="revoke_acknowledgement"
                class="custom-control-label"
                style="font-size: 16px; line-height: 1.5; font-weight: bold; color: #111; margin-bottom: 0; padding-left: 0;">
                I have read, understood and agreed to the above.
              </label>
            </div>

            <div class="d-flex gap-2 pt-2" style="gap: 10px;">
              <button type="button" class="btn btn-danger" data-dismiss="modal">Cancel</button>
              <button
                type="button"
                id="revoke-registry-access-button"
                class="btn btn-registry"
                @click="confirmRevoke"
                :disabled="!revokeAcknowledged || !selectedRevokeUrl"
                :style="{
                  opacity: (!revokeAcknowledged || !selectedRevokeUrl) ? 0.7 : 1,
                  cursor: (!revokeAcknowledged || !selectedRevokeUrl) ? 'not-allowed' : 'pointer',
                }">
                Revoke
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="container-fluid">
      <div class="card bg-light mb-3">
        <center>
          <h2 class="card-header">Registry Users - {{ irr.name }}</h2>
          <p><small>ID # {{ irr.id }}</small></p>
        </center>
        <div class="card-body">
          <div class="card-text">
            <!-- Add Admin User Button (only for registry owners or system admins) -->
            <div v-if="irr.isOwner || permissions.currentPermissions.SYS_GRANT_ADMIN" class="mb-3 d-flex justify-content-end">
              <button
                type="button"
                class="btn btn-registry"
                data-toggle="modal"
                data-target="#addAdminModal">
                Add Admin User
              </button>
            </div>
            
            <div class="card">
              <table class="table table-sm table-borderless table-hover" style="table-layout: fixed;">
                <thead class="border-bottom">
                  <tr>
                    <th class="larger pl-3" scope="col" style="width: 35%;">User</th>
                    <th class="larger" scope="col" style="width: 25%;">Role</th>
                    <!-- <th class="larger" scope="col">Escalate</th> Column for button to escalate Contributor -->
                    <!-- <th class="larger" scope="col">Demote</th> Column for button to demote Admin -->
                    <th v-if="showRevokeColumn" class="larger" scope="col" style="width: 40%;">Revoke</th> <!-- Column for button to delete user -->
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="user in irrUsers" :key="user.id + '-' + user.role">
                    <th class="pl-3" style="width: 35%;">{{user.name}}</th>
                    <th style="width: 25%;">{{user.role_display}}</th>

                    <!-- If an OWNER, and the user is a CONTRIBUTOR, allow escalation -->
                    <!-- <th><a v-if="user.role == 'IRR_CONTRIBUTOR' && permissions.currentPermissions.IRR_GRANT_ADMIN" :href="'/irr/' + irr.id + '/users/escalate/' + user.id + '/'" class="btn btn-success btn-sm" role="button">Grant Admin Access</a></th> -->

                    <!-- If an OWNER, and the user is an ADMIN, allow demotion -->
                    <!-- <th><a v-if="user.role == 'IRR_ADMIN' && permissions.currentPermissions.IRR_GRANT_ADMIN" :href="'/irr/' + irr.id + '/users/demote/' + user.id + '/'" class="btn btn-warning btn-sm" role="button">Remove Admin Access</a></th> -->

                    <!--
                      If an ADMIN, and the user is a CONTRIBUTOR, allow revoke
                      OR
                      If an OWNER or SYSTEM ADMIN, and the user is an ADMIN, allow revoke
                    -->
                      <th v-if="showRevokeColumn" style="width: 40%;"><button v-if="(user.role == 'IRR_CONTRIBUTOR' && permissions.currentPermissions.IRR_GRANT_ACCESS) || (user.role == 'IRR_ADMIN' && (irr.isOwner || permissions.currentPermissions.SYS_GRANT_ADMIN))" type="button" class="btn btn-registry btn-sm" data-toggle="modal" data-target="#revokeRegistryAccessModal" @click="prepareRevoke(user)">Revoke Registry Access</button></th>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
    export default {
      props: {
        route: String,
        csrf_token: String,
        permissions: Object,
        irr: Object,
        users: [Object, Array],
      },
      data() {
        return {
          irrUsers: [],
          adminEmail: '',
          isEmailValid: false,
          isSubmitting: false,
          errorMessage: '',
          successMessage: '',
          selectedRevokeUrl: '',
          selectedRevokeUserName: '',
          revokeAcknowledged: false,
        }
      },
      methods: {
        prepareRevoke(user) {
          this.selectedRevokeUserName = user.name || '';
          this.selectedRevokeUrl = '/irr/' + this.irr.id + '/users/revoke/' + user.id;
          this.revokeAcknowledged = false;
        },
        confirmRevoke() {
          if (!this.revokeAcknowledged || !this.selectedRevokeUrl || !this.csrf_token) {
            return;
          }
          var form = document.createElement('form');
          form.method = 'POST';
          form.action = this.selectedRevokeUrl;
          var method = document.createElement('input');
          method.type = 'hidden';
          method.name = '_method';
          method.value = 'DELETE';
          form.appendChild(method);
          var token = document.createElement('input');
          token.type = 'hidden';
          token.name = '_token';
          token.value = this.csrf_token;
          form.appendChild(token);
          document.body.appendChild(form);
          form.submit();
        },
        validateEmail() {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          this.isEmailValid = emailRegex.test(this.adminEmail);
          // Clear messages when user types
          this.errorMessage = '';
          this.successMessage = '';
        },
        async submitAddAdmin() {
          this.isSubmitting = true;
          this.errorMessage = '';
          this.successMessage = '';

          try {
            const response = await fetch('/irr/users/add-admin', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
              },
              body: JSON.stringify({
                irr_id: this.irr.id,
                admin_email: this.adminEmail,
              }),
            });

            const data = await response.json();

            if (response.ok && data.success) {
              this.successMessage = data.message;
              if (Array.isArray(data.users)) {
                this.irrUsers = data.users;
              }
              this.adminEmail = '';
              this.isEmailValid = false;

              $('#addAdminModal').modal('hide');
            } else {
              this.errorMessage = data.message || 'An error occurred. Please try again.';
            }
          } catch (error) {
            console.error('Error adding admin user:', error);
            this.errorMessage = 'An error occurred while adding the admin user. Please try again.';
          } finally {
            this.isSubmitting = false;
          }
        },
      },
      computed: {
        showRevokeColumn() {
          // Show the Revoke column if:
          // - User can revoke contributors (IRR_GRANT_ACCESS), OR
          // - User is registry owner or system admin (can revoke admins)
          return this.permissions.currentPermissions.IRR_GRANT_ACCESS || 
                 this.irr.isOwner || 
                 this.permissions.currentPermissions.SYS_GRANT_ADMIN;
        }
      },
      mounted() {
        this.irrUsers = Array.isArray(this.users) ? this.users : [];
        $('#revokeRegistryAccessModal').on('hidden.bs.modal', () => {
          this.revokeAcknowledged = false;
          this.selectedRevokeUrl = '';
          this.selectedRevokeUserName = '';
        });
      }
    }
</script>
